// Converts a TipTap JSON document into a LaTeX string.
// Walks the ProseMirror node tree and emits LaTeX for each node type.

export function serializeToLatex(doc: any, fullDocument: boolean = false): string {
    function nodeToTex(n: any): string {
        if (!n) return "";

        switch (n.type) {
            case "doc":
                return (n.content ?? []).map(nodeToTex).join("\n\n").trim();

            case "paragraph": {
                const inner = (n.content ?? []).map(nodeToTex).join("");
                return inner; // blank-line separation is handled by "doc" join
            }

            case "text": {
                let text: string = n.text ?? "";
                // Apply marks outermost-last so they nest correctly
                if (Array.isArray(n.marks)) {
                    for (const mark of n.marks) {
                        if (mark.type === "bold") text = `\\textbf{${text}}`;
                        else if (mark.type === "italic") text = `\\textit{${text}}`;
                        else if (mark.type === "code") text = `\\texttt{${text}}`;
                        else if (mark.type === "strike") text = `\\sout{${text}}`;
                        else if (mark.type === "underline") text = `\\underline{${text}}`;
                        else if (mark.type === "link") text = `\\href{${mark.attrs?.href ?? ""}}{${text}}`;
                    }
                }
                return text;
            }

            case "inlineMath":
                return `$${n.attrs?.latex ?? ""}$`;

            case "displayMath": {
                const latex = n.attrs?.latex ?? "";
                // If the content contains alignment tabs (&), it needs an
                // alignment environment — \[ \] does not support &.
                if (latex.includes("&")) {
                    return `\\begin{align*}\n  ${latex}\n\\end{align*}`;
                }
                return `\\[\n  ${latex}\n\\]`;
            }

            case "numberedEquation":
                return `\\begin{equation}\n  ${n.attrs?.latex ?? ""}\n\\end{equation}`;

            case "docTitle": {
                const text = (n.content ?? []).map(nodeToTex).join("");
                return `\\title{${text}}`;
            }

            case "docAuthor": {
                const text = (n.content ?? []).map(nodeToTex).join("");
                return `\\author{${text}}`;
            }

            case "docDate": {
                const text = (n.content ?? []).map(nodeToTex).join("");
                return `\\date{${text}}`;
            }

            case "docAbstract": {
                const inner = (n.content ?? []).map(nodeToTex).join("\n");
                return `\\begin{abstract}\n${inner}\n\\end{abstract}`;
            }

            case "docToc":
                return `\\tableofcontents`;

            case "theoremBlock": {
                const envType = n.attrs?.envType ?? "theorem";
                const envName = n.attrs?.envName ?? "";
                const inner = (n.content ?? []).map(nodeToTex).join("\n");
                const header = envName ? `\\begin{${envType}}[${envName}]` : `\\begin{${envType}}`;
                return `${header}\n${inner}\n\\end{${envType}}`;
            }

            case "genericEnv": {
                const envName = n.attrs?.envName ?? "environment";
                const inner = (n.content ?? []).map(nodeToTex).join("\n");
                return `\\begin{${envName}}\n${inner}\n\\end{${envName}}`;
            }

            case "twoColumns": {
                const inner = (n.content ?? []).map(nodeToTex).join("\n\n");
                return `\\begin{multicols}{2}\n${inner}\n\\end{multicols}`;
            }

            case "heading": {
                const level: number = n.attrs?.level ?? 1;
                const cmds: Record<number, string> = {
                    1: "\\section",
                    2: "\\subsection",
                    3: "\\subsubsection",
                    4: "\\paragraph",
                    5: "\\subparagraph",
                };
                const cmd = cmds[level] ?? "\\section";
                const text = (n.content ?? []).map(nodeToTex).join("");
                return `${cmd}{${text}}`;
            }

            case "bulletList":
                return (
                    "\\begin{itemize}\n" +
                    (n.content ?? []).map(nodeToTex).join("") +
                    "\\end{itemize}"
                );

            case "orderedList": {
                const styleToLabel: Record<string, string> = {
                    "upper-roman": "\\Roman*",
                    "lower-roman": "\\roman*",
                    "upper-alpha": "\\Alph*",
                    "lower-alpha": "\\alph*",
                };
                const listStyle = n.attrs?.listStyle;
                const labelOpt = listStyle && styleToLabel[listStyle]
                    ? `[label=\\textnormal{(${styleToLabel[listStyle]})}]`
                    : "";
                return (
                    `\\begin{enumerate}${labelOpt}\n` +
                    (n.content ?? []).map(nodeToTex).join("") +
                    "\\end{enumerate}"
                );
            }

            case "listItem": {
                const inner = (n.content ?? []).map(nodeToTex).join("\n");
                return `  \\item ${inner.trim()}\n`;
            }

            case "blockquote": {
                const inner = (n.content ?? []).map(nodeToTex).join("\n");
                return `\\begin{quote}\n${inner.trim()}\n\\end{quote}`;
            }

            case "codeBlock": {
                const inner = (n.content ?? []).map(nodeToTex).join("");
                return `\\begin{verbatim}\n${inner.trimEnd()}\n\\end{verbatim}`;
            }

            case "figureBlock": {
                const fname = n.attrs?.filename ?? "image";
                const cap = n.attrs?.caption ?? "";
                const lbl = n.attrs?.label ?? "";
                const w = n.attrs?.width ?? "0.8";
                let fig = `\\begin{figure}[htbp]\n  \\centering\n  \\includegraphics[width=${w}\\textwidth]{${fname}}`;
                if (cap) fig += `\n  \\caption{${cap}}`;
                if (lbl) fig += `\n  \\label{${lbl}}`;
                fig += `\n\\end{figure}`;
                return fig;
            }

            case "tikzBlock": {
                const tikzSource = n.attrs?.source ?? "";
                const tikzCap = n.attrs?.caption ?? "";
                const tikzLbl = n.attrs?.label ?? "";
                if (tikzCap || tikzLbl) {
                    let fig = `\\begin{figure}[htbp]\n  \\centering\n  ${tikzSource}`;
                    if (tikzCap) fig += `\n  \\caption{${tikzCap}}`;
                    if (tikzLbl) fig += `\n  \\label{${tikzLbl}}`;
                    fig += `\n\\end{figure}`;
                    return fig;
                }
                return `\\begin{center}\n  ${tikzSource}\n\\end{center}`;
            }

            case "horizontalRule":
                return "\\hrule";

            case "hardBreak":
                return "\\\\\n";

            default:
                // Unknown node — recurse into children
                return (n.content ?? []).map(nodeToTex).join("");
        }
    }

    // Extract metadata nodes and body content separately
    function extractMetadata(doc: any): { title: string; author: string; date: string; body: any[] } {
        let title = "";
        let author = "";
        let date = "";
        const bodyContent: any[] = [];

        for (const node of doc.content ?? []) {
            if (node.type === "docTitle") {
                title = (node.content ?? []).map(nodeToTex).join("");
            } else if (node.type === "docAuthor") {
                author = (node.content ?? []).map(nodeToTex).join("");
            } else if (node.type === "docDate") {
                date = (node.content ?? []).map(nodeToTex).join("");
            } else {
                bodyContent.push(node);
            }
        }

        return { title, author, date, body: bodyContent };
    }

    const bodyContent = nodeToTex(doc);

    if (!fullDocument) {
        return bodyContent;
    }

    // Extract metadata for preamble
    const metadata = extractMetadata(doc);
    const bodyOnly = metadata.body.map(nodeToTex).join("\n\n").trim();

    // Wrap in full LaTeX document with amsart preamble
    return `\\documentclass[11pt]{amsart}

% ---------- Packages ----------
\\usepackage{amsmath,amssymb,amsthm}
\\usepackage{amsrefs}
\\usepackage{tikz}
\\usepackage{tikz-cd}
\\usetikzlibrary{arrows,arrows.meta,calc,decorations.pathreplacing}
\\usepackage[all,cmtip]{xy}
\\usepackage{color}
\\usepackage[hidelinks]{hyperref}
\\usepackage{enumitem}
\\usepackage{booktabs}
\\usepackage{graphicx}

% ---------- Theorem environments ----------
\\theoremstyle{plain}
\\newtheorem{theorem}{Theorem}[section]
\\newtheorem{lemma}[theorem]{Lemma}
\\newtheorem{proposition}[theorem]{Proposition}
\\newtheorem{corollary}[theorem]{Corollary}

\\theoremstyle{definition}
\\newtheorem{definition}[theorem]{Definition}
\\newtheorem{example}[theorem]{Example}
\\newtheorem{exercise}[theorem]{Exercise}

\\theoremstyle{remark}
\\newtheorem{remark}[theorem]{Remark}

% ---------- Document Metadata ----------
\\title{${metadata.title || "Untitled"}}
\\author{${metadata.author || ""}}
\\date{${metadata.date || "\\today"}}

\\begin{document}

\\maketitle

${bodyOnly}

\\end{document}
`;
}
